/**
 * Clearinghouse Settings Page
 *
 * Allows organizations to configure their claims clearinghouse settings
 */

import { useState, useEffect } from 'react';
import { ClearinghouseSettings } from '@/components/billing/ClearinghouseSettings';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer, PageState } from '@/components/weavein';

import { useAppTranslation } from '@/contexts/TranslationContext';
export default function ClearinghousePage() {
  const { t } = useAppTranslation();
  const [organization, setOrganization] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrganization();
  }, []);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(cboId && { 'X-CBO-ID': cboId })
    };
  };

  const fetchOrganization = async () => {
    try {
      // Get organization ID from localStorage (already set during login/org selection)
      const cboId = localStorage.getItem('active_cbo_id');

      if (cboId) {
        // Fetch organization details directly
        const orgResponse = await fetch(getWeaveinApiUrl(`/api/cbos/${cboId}`), {
          credentials: 'include',
          headers: getAuthHeaders()
        });

        if (orgResponse.ok) {
          const orgData = await orgResponse.json();
          setOrganization(orgData);
        }
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
    } finally {
      setLoading(false);
    }
  };

  const header = (
    <PageHeader
      eyebrow="Admin · Billing"
      title={t('clearinghouse.title', 'Clearinghouse')}
      description={t(
        'clearinghouse.description',
        'Configure your claims clearinghouse connection and submission settings'
      )}
    />
  );

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <PageContainer>
          <PageState
            variant="loading"
            title={t('clearinghouse.loading_organization', 'Loading organization...')}
          />
        </PageContainer>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <PageContainer>
          <PageState
            variant="empty"
            title={t('clearinghouse.no_organization_found', 'No Organization Found')}
            message={t(
              'clearinghouse.you_must_be_associated_with',
              'You must be associated with an organization to configure clearinghouse settings.'
            )}
          />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {header}
      <PageContainer>
        <ClearinghouseSettings
          organizationId={organization.id}
          organizationName={organization.name}
        />
      </PageContainer>
    </div>
  );
}
